import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Calendar, Image as ImageIcon, Upload, Download,
  Loader2, CheckCircle, AlertCircle, GripVertical, X,
  LayoutGrid, Smartphone, RefreshCw, Copy, RectangleVertical,
  Star, ChevronLeft, ChevronRight, Eye, Plus, Clock, Send,
  Video, Play, Crosshair, Move, Maximize2, Sparkles, Save,
  Bell, BellOff, Settings2, FileText, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';
import { AppHeader } from './AppHeader';
import PhonePreview from './PhonePreview';
import './SocialWeek.css';

const API = process.env.REACT_APP_BACKEND_URL;
axios.defaults.withCredentials = true;

// Helper for push notification subscription
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}

const SIZE_DEFS = [
  { key: 'square', label: '1:1', w: 1080, h: 1080, Icon: LayoutGrid },
  { key: 'portrait', label: '4:5', w: 1080, h: 1350, Icon: RectangleVertical },
  { key: 'story', label: '9:16', w: 1080, h: 1920, Icon: Smartphone },
];

let _slotUid = 0;
const nextUid = () => ++_slotUid;

function makeSlot(template) {
  const defaultSizes = { square: false, portrait: false, story: false };
  const firstActive = template.format === 'story' ? 'story' : 'portrait';
  const isCollage = template.id === 'collage';
  return {
    uid: nextUid(),
    templateId: template.id,
    templateName: template.name,
    description: template.description,
    listing: null,
    sizes: defaultSizes,
    activeTab: firstActive,  // which size tab is being viewed
    images: [],
    imagesLoading: false,
    selectedImageUrl: null,
    perSizeImages: { square: null, portrait: null, story: null }, // per-tab image selection
    perSizeFocalPoints: { square: { x: 50, y: 50 }, portrait: { x: 50, y: 50 }, story: { x: 50, y: 50 } },
    stackImages: ['', '', ''],
    focalPoint: { x: 50, y: 50 },
    isCollage,
    collagePanelCount: isCollage ? (firstActive === 'story' ? 3 : 2) : 0,
    collageImages: { portrait: ['', ''], story: ['', '', ''] },
    collageImageIdx: { portrait: [0, 0], story: [0, 0, 0] },
    collageFocalPoints: {
      portrait: [{ x: 50, y: 50 }, { x: 50, y: 50 }],
      story: [{ x: 50, y: 50 }, { x: 50, y: 50 }, { x: 50, y: 50 }],
    },
  };
}

// ── Property Bin ──
function PropertyBin({ listings, thumbnails, onDragStart, loading: listingsLoading, scanning, onAddOrderId }) {
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showAutoLoad, setShowAutoLoad] = useState(false);
  const [dateRange, setDateRange] = useState('5w');
  const searchTimeout = useRef(null);

  // Debounced address search against WMS
  const handleSearchChange = (val) => {
    setSearch(val);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (val.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.get(`${API}/api/social-week/search?q=${encodeURIComponent(val.trim())}`);
        setSearchResults(res.data.results || []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 400);
  };

  const handleAddFromSearch = async (result) => {
    try {
      const res = await axios.get(`${API}/api/social-week/lookup-order/${result.order_id}`);
      onAddOrderId(res.data);
      toast.success(`Added: ${result.full_address || `Order #${result.order_id}`}`);
      setSearch('');
      setSearchResults([]);
    } catch {
      toast.error(`Failed to add order #${result.order_id}`);
    }
  };

  // Filter manually-added listings in the bin
  const binSearch = search.toLowerCase();
  const now = Date.now();
  const dateFilterMs = { all: 0, '1w': 7, '2w': 14, '5w': 35, '1m': 30, '3m': 90 }[dateRange] * 86400000;
  const dateFiltered = dateFilterMs
    ? listings.filter(l => {
        const d = l.photo_completed_at || l.created_at;
        return d && (now - new Date(d).getTime()) <= dateFilterMs;
      })
    : listings;
  const filteredBin = binSearch.length >= 2 ? [] : dateFiltered;

  return (
    <div className="sw-property-bin" data-testid="sw-property-bin">
      <div className="sw-bin-header">
        <h3>Properties</h3>
        <span className="sw-bin-count">{listings.length}</span>
      </div>

      {/* Primary: Search by address or order ID */}
      <div className="sw-search-primary">
        <input
          className="sw-bin-search"
          placeholder="Enter order ID or search address..."
          value={search}
          onChange={e => handleSearchChange(e.target.value)}
          data-testid="sw-property-search"
        />
        {searching && <Loader2 size={14} className="spin sw-search-spinner" />}
      </div>

      {/* Search results dropdown */}
      {searchResults.length > 0 && (
        <div className="sw-search-results" data-testid="sw-search-results">
          {searchResults.map(r => (
            <button
              key={r.order_id}
              className={`sw-search-item ${!r.has_photos ? 'sw-search-no-photos' : ''}`}
              onClick={() => handleAddFromSearch(r)}
              data-testid={`sw-search-result-${r.order_id}`}
            >
              <div className="sw-search-info">
                <span className="sw-search-address">{r.full_address || `Order #${r.order_id}`}</span>
                <span className="sw-search-meta">
                  #{r.order_id}
                  {r.branch_name && ` · ${r.branch_name}`}
                </span>
              </div>
              {r.has_photos ? (
                <span className="sw-search-badge sw-badge-ready"><CheckCircle size={12} /> Photos</span>
              ) : (
                <span className="sw-search-badge sw-badge-pending"><AlertCircle size={12} /> No photos</span>
              )}
            </button>
          ))}
        </div>
      )}

      {search.trim().length >= 2 && !searching && searchResults.length === 0 && (
        <div className="sw-search-no-results">No results for "{search}"</div>
      )}

      {/* Date range filter */}
      {listings.length > 0 && !search && (
        <div className="sw-date-pills" data-testid="sw-date-pills">
          {[
            { key: 'all', label: 'All' },
            { key: '1w', label: '1 wk' },
            { key: '2w', label: '2 wk' },
            { key: '5w', label: '5 wk' },
            { key: '3m', label: '3 mo' },
          ].map(d => (
            <button
              key={d.key}
              className={`sw-date-pill ${dateRange === d.key ? 'sw-date-pill-active' : ''}`}
              onClick={() => setDateRange(d.key)}
              data-testid={`sw-date-${d.key}`}
            >
              {d.label}
            </button>
          ))}
        </div>
      )}

      {/* Properties already added */}
      <div className="sw-bin-list">
        {filteredBin.length === 0 && !search && (
          <div className="sw-bin-empty">{dateRange !== 'all' ? 'No properties in this date range' : 'Search above to add properties'}</div>
        )}
        {filteredBin.map(listing => {
          const thumb = thumbnails[listing.order_id] || thumbnails[String(listing.order_id)];
          return (
          <div
            key={listing.order_id}
            className="sw-bin-item"
            draggable
            onDragStart={e => {
              e.dataTransfer.setData('application/json', JSON.stringify(listing));
              e.dataTransfer.effectAllowed = 'copy';
              onDragStart?.(listing);
            }}
            data-testid={`sw-property-${listing.order_id}`}
          >
            <GripVertical size={14} className="sw-grip" />
            <div className={`sw-bin-thumb ${thumb ? '' : 'sw-bin-thumb-loading'}`}>
              {thumb ? (
                <img
                  src={thumb}
                  alt=""
                  className="sw-bin-thumb-img"
                  loading="lazy"
                  decoding="async"
                  onError={e => { e.currentTarget.style.display = 'none'; }}
                />
              ) : (
                <ImageIcon size={16} className="sw-bin-thumb-icon" />
              )}
            </div>
            <div className="sw-bin-info">
              <span className="sw-bin-address">{listing.full_address || `Order #${listing.order_id}`}</span>
              <span className="sw-bin-meta">{listing.price ? `£${Number(String(listing.price).replace(/,/g, '')).toLocaleString()}` : `#${listing.order_id}`}</span>
            </div>
          </div>
          );
        })}
      </div>

      {/* Auto-load section — greyed out for future */}
      <div className="sw-autoload-section">
        <button
          className="sw-autoload-toggle"
          onClick={() => setShowAutoLoad(!showAutoLoad)}
          data-testid="sw-autoload-toggle"
        >
          <RefreshCw size={13} />
          <span>Auto-load from branch</span>
          <span className="sw-coming-soon">Coming soon</span>
        </button>
        {showAutoLoad && (
          <div className="sw-autoload-content">
            {listingsLoading && (
              <div className="sw-autoload-status">
                <Loader2 size={14} className="spin" />
                {scanning ? 'Scanning completed orders...' : 'Loading...'}
              </div>
            )}
            {!listingsLoading && (
              <div className="sw-autoload-status">
                <span>Auto-loading branch properties is being improved. Use search above for now.</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Branch Videos (Social Video integration) ──
function BranchVideos({ videos, loading }) {
  const [playingUrl, setPlayingUrl] = useState(null);
  const [copiedCaption, setCopiedCaption] = useState(null);

  const copyCaption = (caption, i) => {
    navigator.clipboard.writeText(caption);
    setCopiedCaption(i);
    setTimeout(() => setCopiedCaption(null), 2000);
  };

  if (loading) {
    return (
      <div className="sw-videos-section" data-testid="sw-branch-videos">
        <div className="sw-bin-header">
          <h3><Video size={15} /> Social Videos</h3>
        </div>
        <div className="sw-videos-loading">
          <Loader2 size={14} className="spin" />
          <span>Loading videos...</span>
        </div>
      </div>
    );
  }

  if (!videos || videos.length === 0) {
    return (
      <div className="sw-videos-section" data-testid="sw-branch-videos">
        <div className="sw-bin-header">
          <h3><Video size={15} /> Social Videos</h3>
          <span className="sw-bin-count">0</span>
        </div>
        <div className="sw-videos-empty">
          <Video size={18} />
          <span>No videos yet</span>
          <span className="sw-videos-hint">Submit videos from the Video Builder to see them here</span>
        </div>
      </div>
    );
  }

  return (
    <div className="sw-videos-section" data-testid="sw-branch-videos">
      <div className="sw-bin-header">
        <h3><Video size={15} /> Social Videos</h3>
        <span className="sw-bin-count">{videos.length}</span>
      </div>
      <div className="sw-videos-list">
        {videos.map((vid, i) => (
          <div
            key={vid.key || i}
            className="sw-video-item sw-video-draggable"
            draggable
            onDragStart={e => {
              e.dataTransfer.setData('application/json', JSON.stringify({
                isVideo: true,
                url: vid.url,
                filename: vid.display_name || vid.filename,
                key: vid.key,
                size: vid.size,
                caption: vid.caption,
                thumbnail_url: vid.thumbnail_url,
              }));
              e.dataTransfer.effectAllowed = 'copy';
            }}
            data-testid={`sw-video-${i}`}
          >
            <GripVertical size={14} className="sw-grip" />
            <div className="sw-video-thumb" onClick={() => setPlayingUrl(playingUrl === vid.url ? null : vid.url)}>
              {playingUrl === vid.url ? (
                <video
                  src={vid.url}
                  autoPlay
                  controls
                  className="sw-video-player"
                  onEnded={() => setPlayingUrl(null)}
                />
              ) : vid.thumbnail_url ? (
                <img src={vid.thumbnail_url} alt={vid.display_name || vid.filename} className="sw-video-thumb-img" />
              ) : (
                <>
                  <Play size={20} className="sw-video-play-icon" />
                  <Video size={16} className="sw-video-icon-bg" />
                </>
              )}
            </div>
            <div className="sw-video-info">
              <span className="sw-video-name" title={vid.display_name || vid.filename}>{vid.display_name || vid.filename}</span>
              <span className="sw-video-meta">
                {vid.size ? `${(vid.size / (1024 * 1024)).toFixed(1)} MB` : ''}
                <span className="sw-video-drag-hint">Drag to slot</span>
              </span>
              {vid.caption && (
                <button
                  className="sw-video-caption-btn"
                  onClick={() => copyCaption(vid.caption, i)}
                  title="Copy social caption"
                  data-testid={`sw-video-caption-${i}`}
                >
                  {copiedCaption === i
                    ? <><CheckCircle size={11} /> Copied</>
                    : <><Copy size={11} /> Caption</>
                  }
                </button>
              )}
            </div>
            <a
              href={vid.url}
              download={vid.filename}
              className="sw-video-dl"
              title="Download"
              data-testid={`sw-video-dl-${i}`}
            >
              <Download size={13} />
            </a>
          </div>
        ))}
      </div>

      {playingUrl && (
        <div className="sw-video-overlay" onClick={() => setPlayingUrl(null)} data-testid="sw-video-overlay">
          <div className="sw-video-modal" onClick={e => e.stopPropagation()}>
            <button className="sw-video-modal-close" onClick={() => setPlayingUrl(null)}><X size={18} /></button>
            <video src={playingUrl} autoPlay controls className="sw-video-modal-player" />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Focal Point Modal (Large view for precision) ──
function FocalPointModal({ imageUrl, focalPoint, onSetFocalPoint, onClose }) {
  const imgRef = useRef(null);

  const handleClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    onSetFocalPoint({ x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 });
  };

  return (
    <div className="sw-focal-overlay" onClick={onClose} data-testid="sw-focal-modal">
      <div className="sw-focal-modal" onClick={e => e.stopPropagation()}>
        <div className="sw-focal-modal-header">
          <div className="sw-focal-modal-title">
            <Crosshair size={15} />
            <span>Set Focal Point</span>
          </div>
          <span className="sw-focal-hint">Click on the image where the main subject is</span>
          <button className="sw-focal-modal-close" onClick={onClose} data-testid="sw-focal-modal-close"><X size={16} /></button>
        </div>
        <div className="sw-focal-modal-body">
          <div className="sw-focal-img-wrap" onClick={handleClick} data-testid="sw-focal-img-large">
            <img ref={imgRef} src={imageUrl} alt="Set focal point" className="sw-focal-img-large" draggable={false} />
            <div
              className="sw-focal-crosshair sw-focal-crosshair-large"
              style={{ left: `${focalPoint.x}%`, top: `${focalPoint.y}%` }}
              data-testid="sw-focal-crosshair-large"
            >
              <div className="sw-focal-ring" />
              <div className="sw-focal-line sw-focal-line-h" />
              <div className="sw-focal-line sw-focal-line-v" />
            </div>
          </div>
          <div className="sw-focal-previews">
            <div className="sw-focal-preview-label">Crop preview:</div>
            <div className="sw-focal-preview-row">
              {[
                { label: '4:5', w: 80, h: 100 },
                { label: '9:16', w: 56, h: 100 },
                { label: '1:1', w: 80, h: 80 },
              ].map(p => (
                <div key={p.label} className="sw-focal-preview-card">
                  <div
                    className="sw-focal-preview-img"
                    style={{
                      width: p.w,
                      height: p.h,
                      backgroundImage: `url(${imageUrl})`,
                      backgroundSize: 'cover',
                      backgroundPosition: `${focalPoint.x}% ${focalPoint.y}%`,
                    }}
                  />
                  <span className="sw-focal-preview-label-text">{p.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Image Carousel (with Focal Point) ──
function ImageCarousel({ images, selectedUrl, onSelect, loading, focalPoint, onFocalPointChange }) {
  const [idx, setIdx] = useState(0);
  const [showFocalModal, setShowFocalModal] = useState(false);

  // Reset index when images change
  useEffect(() => { setIdx(0); }, [images]);

  // Auto-select the currently displayed image when user navigates
  useEffect(() => {
    if (images && images.length > 0 && images[idx]) {
      const currentUrl = images[idx].url;
      if (currentUrl && currentUrl !== selectedUrl) {
        onSelect(currentUrl);
      }
    }
  }, [idx, images]); // only re-run when user navigates or images change

  const handleImageClick = (e) => {
    if (!onFocalPointChange) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    onFocalPointChange({ x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 });
  };

  if (loading) {
    return (
      <div className="sw-carousel sw-carousel-loading">
        <Loader2 size={18} className="spin" />
        <span>Loading photos...</span>
      </div>
    );
  }

  if (!images || images.length === 0) {
    return (
      <div className="sw-carousel sw-carousel-empty">
        <ImageIcon size={20} />
        <span>No photos available</span>
      </div>
    );
  }

  const current = images[idx];
  const isSelected = current?.url === selectedUrl;
  const fp = focalPoint || { x: 50, y: 50 };
  const isCustomFocal = fp.x !== 50 || fp.y !== 50;

  return (
    <div className="sw-carousel" data-testid="sw-image-carousel">
      <div className="sw-carousel-viewport" onClick={handleImageClick} style={{ cursor: 'crosshair' }}>
        <img
          src={current.url}
          alt={`Photo ${current.photo_num}`}
          className="sw-carousel-img"
          draggable={false}
        />
        {/* Focal point crosshair marker */}
        <div
          className="sw-focal-crosshair"
          style={{ left: `${fp.x}%`, top: `${fp.y}%` }}
          data-testid="sw-focal-crosshair"
        >
          <div className="sw-focal-ring" />
          <div className="sw-focal-line sw-focal-line-h" />
          <div className="sw-focal-line sw-focal-line-v" />
        </div>
        <div className="sw-carousel-overlay">
          <button
            className={`sw-star-btn ${isSelected ? 'sw-star-active' : ''}`}
            onClick={(e) => { e.stopPropagation(); onSelect(current.url); }}
            title={isSelected ? 'Selected for generation' : 'Use this photo'}
            data-testid={`sw-star-${current.photo_num}`}
          >
            <Star size={16} fill={isSelected ? '#f59e0b' : 'none'} />
          </button>
          <div className="sw-carousel-overlay-right">
            <button
              className={`sw-focal-expand-btn ${isCustomFocal ? 'sw-focal-active' : ''}`}
              onClick={(e) => { e.stopPropagation(); setShowFocalModal(true); }}
              title="Set crop focus point (large view)"
              data-testid="sw-focal-expand"
            >
              <Crosshair size={13} />
            </button>
            <span className="sw-carousel-counter">{idx + 1} / {images.length}</span>
          </div>
        </div>
        {images.length > 1 && (
          <>
            <button
              className="sw-carousel-arrow sw-carousel-prev"
              onClick={(e) => { e.stopPropagation(); setIdx(i => (i - 1 + images.length) % images.length); }}
              data-testid="sw-carousel-prev"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              className="sw-carousel-arrow sw-carousel-next"
              onClick={(e) => { e.stopPropagation(); setIdx(i => (i + 1) % images.length); }}
              data-testid="sw-carousel-next"
            >
              <ChevronRight size={18} />
            </button>
          </>
        )}
      </div>
      {/* Dot indicators for quick navigation */}
      {images.length > 1 && images.length <= 15 && (
        <div className="sw-carousel-dots">
          {images.map((img, i) => (
            <button
              key={i}
              className={`sw-dot ${i === idx ? 'sw-dot-active' : ''} ${img.url === selectedUrl ? 'sw-dot-starred' : ''}`}
              onClick={() => setIdx(i)}
            />
          ))}
        </div>
      )}
      {showFocalModal && current?.url && (
        <FocalPointModal
          imageUrl={current.url}
          focalPoint={fp}
          onSetFocalPoint={onFocalPointChange}
          onClose={() => setShowFocalModal(false)}
        />
      )}
    </div>
  );
}

// ── Size Toggles — pure on/off chips (no preview side-effects) ──
function SizeToggles({ sizes, onChange, activeTab, onTabChange }) {
  return (
    <div className="sw-size-toggles" data-testid="sw-size-toggles">
      {SIZE_DEFS.map(s => {
        const isOn = sizes[s.key];
        return (
          <button
            key={s.key}
            type="button"
            className={`sw-size-pill ${isOn ? 'sw-size-on' : 'sw-size-off'}`}
            onClick={() => {
              const newSizes = { ...sizes, [s.key]: !isOn };
              onChange(newSizes);
              // Keep activeTab pointing at an enabled size.
              if (!isOn) {
                // just turned ON
                if (!sizes[activeTab]) onTabChange(s.key);
              } else {
                // just turned OFF
                if (activeTab === s.key) {
                  const next = SIZE_DEFS.find(d => newSizes[d.key]);
                  if (next) onTabChange(next.key);
                }
              }
            }}
            data-testid={`sw-size-${s.key}`}
            aria-pressed={isOn}
            title={`${isOn ? 'Remove' : 'Add'} ${s.label} (${s.w}×${s.h})`}
          >
            <s.Icon size={12} />
            <span>{s.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// ── Preview Cycler — appears only when 2+ sizes are on, lets you flip between their previews ──
function PreviewCycler({ sizes, activeTab, onTabChange }) {
  const enabled = SIZE_DEFS.filter(s => sizes[s.key]);
  if (enabled.length < 2) return null;
  const currentIdx = Math.max(0, enabled.findIndex(s => s.key === activeTab));
  const go = (delta) => {
    const next = enabled[(currentIdx + delta + enabled.length) % enabled.length];
    onTabChange(next.key);
  };
  const current = enabled[currentIdx] || enabled[0];
  return (
    <div className="sw-preview-cycler" data-testid="sw-preview-cycler">
      <button
        type="button"
        className="sw-pc-arrow"
        onClick={() => go(-1)}
        aria-label="Previous size"
        data-testid="sw-pc-prev"
      >
        <ChevronLeft size={13} />
      </button>
      <span className="sw-pc-label">
        <Eye size={11} />
        <strong>{current.label}</strong>
        <em>{currentIdx + 1}/{enabled.length}</em>
      </span>
      <button
        type="button"
        className="sw-pc-arrow"
        onClick={() => go(1)}
        aria-label="Next size"
        data-testid="sw-pc-next"
      >
        <ChevronRight size={13} />
      </button>
    </div>
  );
}

// ── Collage Image Picker (per-panel carousel with focal points) ──
function CollageImagePicker({ images, panelCount, collageImages, collageImageIdx, collageFocalPoints, onUpdate, loading }) {
  const [focalModalPanel, setFocalModalPanel] = useState(null);

  // Auto-assign images to panels if not yet set (must be before conditional returns)
  useEffect(() => {
    if (!loading && images && images.length > 0 && (!collageImages || collageImages.length === 0 || collageImages.every(u => !u))) {
      const newImages = [];
      const newIdxArr = [];
      for (let i = 0; i < panelCount; i++) {
        const idx = i % images.length;
        newImages.push(images[idx]?.url || '');
        newIdxArr.push(idx);
      }
      onUpdate({ collageImages: newImages, collageImageIdx: newIdxArr });
    }
  }, [images, loading, panelCount]);

  if (loading) {
    return <div className="sw-carousel sw-carousel-loading"><Loader2 size={18} className="spin" /><span>Loading photos...</span></div>;
  }
  if (!images || images.length === 0) {
    return <div className="sw-carousel sw-carousel-empty"><ImageIcon size={20} /><span>No photos available</span></div>;
  }

  const navigatePanel = (panelIdx, dir) => {
    const newIdx = (collageImageIdx[panelIdx] + dir + images.length) % images.length;
    const newImages = [...collageImages];
    const newIdxArr = [...collageImageIdx];
    newImages[panelIdx] = images[newIdx]?.url || '';
    newIdxArr[panelIdx] = newIdx;
    onUpdate({ collageImages: newImages, collageImageIdx: newIdxArr });
  };

  const setFocalPoint = (panelIdx, fp) => {
    const newFPs = [...collageFocalPoints];
    newFPs[panelIdx] = fp;
    onUpdate({ collageFocalPoints: newFPs });
  };

  const panelClass = panelCount === 3 ? 'sw-collage-panel-3' : 'sw-collage-panel-2';
  const labels = panelCount === 3 ? ['Top', 'Middle', 'Bottom'] : ['Top', 'Bottom'];

  return (
    <div className="sw-collage-picker" data-testid="sw-collage-picker">
      <div className="sw-collage-label">Pick {panelCount} images for collage:</div>
      <div className="sw-collage-panels">
        {Array.from({ length: panelCount }).map((_, i) => {
          const url = collageImages[i];
          const fp = collageFocalPoints[i] || { x: 50, y: 50 };
          const isCustomFocal = fp.x !== 50 || fp.y !== 50;
          return (
            <div key={i} className={`sw-collage-panel ${panelClass}`} data-testid={`sw-collage-panel-${i}`}>
              {url ? (
                <img
                  src={url}
                  alt={`${labels[i]} panel`}
                  className="sw-collage-panel-img"
                  style={{ objectPosition: `${fp.x}% ${fp.y}%` }}
                />
              ) : (
                <div className="sw-collage-panel-empty">
                  <ImageIcon size={12} />
                  <span>{labels[i]}</span>
                </div>
              )}
              <div className="sw-collage-panel-controls">
                <div className="sw-collage-nav">
                  <button className="sw-collage-nav-btn" onClick={() => navigatePanel(i, -1)} data-testid={`sw-collage-prev-${i}`}>
                    <ChevronLeft size={13} />
                  </button>
                  <button className="sw-collage-nav-btn" onClick={() => navigatePanel(i, 1)} data-testid={`sw-collage-next-${i}`}>
                    <ChevronRight size={13} />
                  </button>
                </div>
                <span className="sw-collage-counter">{labels[i]}: {(collageImageIdx[i] || 0) + 1}/{images.length}</span>
                <button
                  className={`sw-collage-focal-btn ${isCustomFocal ? 'sw-collage-focal-active' : ''}`}
                  onClick={() => setFocalModalPanel(i)}
                  title={`Set focal point for ${labels[i]} panel`}
                  data-testid={`sw-collage-focal-${i}`}
                >
                  <Crosshair size={11} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
      {focalModalPanel !== null && collageImages[focalModalPanel] && (
        <FocalPointModal
          imageUrl={collageImages[focalModalPanel]}
          focalPoint={collageFocalPoints[focalModalPanel] || { x: 50, y: 50 }}
          onSetFocalPoint={(fp) => setFocalPoint(focalModalPanel, fp)}
          onClose={() => setFocalModalPanel(null)}
        />
      )}
    </div>
  );
}

// ── Template Slot Card ──
function TemplateSlotCard({ slot, thumbnail, onDrop, onRemove, onDuplicate, onDelete, onSizesChange, onSelectImage, onFocalPointChange, onUpdateCollage, onTabChange }) {
  const [dragOver, setDragOver] = useState(false);
  const activeSizeCount = Object.values(slot.sizes).filter(Boolean).length;
  const needsSize = slot.listing && activeSizeCount === 0;

  return (
    <div
      className={`sw-slot ${dragOver ? 'sw-slot-dragover' : ''} ${slot.listing ? 'sw-slot-filled' : ''} ${needsSize ? 'sw-slot-needs-size' : ''}`}
      onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={e => {
        e.preventDefault();
        setDragOver(false);
        try {
          const listing = JSON.parse(e.dataTransfer.getData('application/json'));
          onDrop(slot.uid, listing);
        } catch { }
      }}
      data-testid={`sw-slot-${slot.uid}`}
    >
      <div className="sw-slot-header">
        <span className="sw-slot-name">{slot.templateName}</span>
        <div className="sw-slot-actions">
          <button className="sw-slot-dup" onClick={() => onDuplicate(slot.uid)} title="Duplicate slot" data-testid={`sw-dup-${slot.uid}`}>
            <Copy size={13} />
          </button>
          {slot.listing && (
            <button className="sw-slot-remove" onClick={() => onRemove(slot.uid)} title="Remove property" data-testid={`sw-remove-${slot.uid}`}>
              <X size={13} />
            </button>
          )}
          <button className="sw-slot-delete" onClick={() => onDelete(slot.uid)} title="Delete slot" data-testid={`sw-delete-${slot.uid}`}>
            <X size={13} />
          </button>
        </div>
      </div>

      {needsSize && (
        <div className="sw-slot-size-banner" data-testid={`sw-slot-needs-size-${slot.uid}`}>
          <AlertTriangle size={11} />
          <span>Pick a size below to include this in Generate</span>
        </div>
      )}

      {slot.listing ? (
        slot.listing.isVideo ? (
          <div className="sw-slot-video-content" data-testid={`sw-slot-video-${slot.uid}`}>
            <div className="sw-slot-video-label">
              <Video size={13} />
              <span>{slot.listing.filename || 'Social Video'}</span>
            </div>
            <div className="sw-slot-video-player-wrap">
              {slot.listing.thumbnail_url ? (
                <img src={slot.listing.thumbnail_url} alt="" className="sw-slot-video-thumb" />
              ) : (
                <video
                  src={slot.listing.url}
                  controls
                  preload="metadata"
                  className="sw-slot-video-el"
                />
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="sw-slot-property-label">
              {slot.listing.full_address || `#${slot.listing.order_id}`}
            </div>
            <PreviewCycler
              sizes={slot.sizes}
              activeTab={slot.activeTab}
              onTabChange={tab => onTabChange(slot.uid, tab)}
            />
            {slot.isCollage ? (
              <CollageImagePicker
                images={slot.images}
                panelCount={slot.activeTab === 'story' ? 3 : 2}
                collageImages={slot.collageImages?.[slot.activeTab] || []}
                collageImageIdx={slot.collageImageIdx?.[slot.activeTab] || []}
                collageFocalPoints={slot.collageFocalPoints?.[slot.activeTab] || []}
                onUpdate={(updates) => {
                  // Store per-tab collage data
                  const tab = slot.activeTab;
                  const merged = {};
                  if (updates.collageImages) merged.collageImages = { ...slot.collageImages, [tab]: updates.collageImages };
                  if (updates.collageImageIdx) merged.collageImageIdx = { ...slot.collageImageIdx, [tab]: updates.collageImageIdx };
                  if (updates.collageFocalPoints) merged.collageFocalPoints = { ...slot.collageFocalPoints, [tab]: updates.collageFocalPoints };
                  onUpdateCollage(slot.uid, merged);
                }}
                loading={slot.imagesLoading}
              />
            ) : (
              <ImageCarousel
                images={slot.images}
                selectedUrl={slot.perSizeImages?.[slot.activeTab] || slot.selectedImageUrl}
                onSelect={(url) => onSelectImage(slot.uid, url)}
                loading={slot.imagesLoading}
                focalPoint={slot.perSizeFocalPoints?.[slot.activeTab] || slot.focalPoint}
                onFocalPointChange={(fp) => onFocalPointChange(slot.uid, fp)}
              />
            )}
          </>
        )
      ) : (
        <div className="sw-slot-empty">
          <ImageIcon size={24} />
          <span>Drop a property or video here</span>
        </div>
      )}

      <div className="sw-slot-footer">
        <SizeToggles
          sizes={slot.sizes}
          activeTab={slot.activeTab}
          onChange={newSizes => onSizesChange(slot.uid, newSizes)}
          onTabChange={tab => onTabChange(slot.uid, tab)}
        />
        <span className="sw-slot-img-count" data-testid={`sw-count-${slot.uid}`}>{activeSizeCount} img{activeSizeCount !== 1 ? 's' : ''}</span>
      </div>

      <div className="sw-slot-desc">{slot.description}</div>
    </div>
  );
}

// ── Logo Uploader ──
function LogoUploader({ logos, onUpload, uploading }) {
  const darkRef = useRef(null);
  const lightRef = useRef(null);

  const handleFile = async (file, variant) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('variant', variant);
    await onUpload(formData, variant);
  };

  return (
    <div className="sw-logo-section" data-testid="sw-logo-section">
      <h3>Agency Logos</h3>
      <p className="sw-logo-hint">Upload dark & light versions for template overlays</p>
      <div className="sw-logo-grid">
        {['dark', 'light'].map(variant => (
          <div key={variant} className={`sw-logo-card sw-logo-${variant}`}>
            <span className="sw-logo-label">{variant} version</span>
            {logos[variant] ? (
              <div className="sw-logo-preview">
                <img src={`${API}/api/social-week/output/logos/${logos[variant].split('/logos/')[1]}`} alt={`${variant} logo`} onError={e => e.target.style.display = 'none'} />
                <CheckCircle size={14} className="sw-logo-check" />
              </div>
            ) : (
              <button className="sw-logo-upload-btn" onClick={() => variant === 'dark' ? darkRef.current?.click() : lightRef.current?.click()} disabled={uploading} data-testid={`sw-upload-logo-${variant}`}>
                {uploading ? <Loader2 size={16} className="spin" /> : <Upload size={16} />}
                Upload
              </button>
            )}
          </div>
        ))}
      </div>
      <input ref={darkRef} type="file" accept="image/*" hidden onChange={e => handleFile(e.target.files[0], 'dark')} />
      <input ref={lightRef} type="file" accept="image/*" hidden onChange={e => handleFile(e.target.files[0], 'light')} />
    </div>
  );
}

// ── Output Gallery ──
function OutputGallery({ outputs }) {
  const downloadAll = () => {
    outputs.forEach(o => {
      if (!o.url) return;
      const a = document.createElement('a');
      a.href = `${API}${o.url}`;
      a.download = o.filename;
      a.click();
    });
  };

  const sizeLabel = fmt => ({ square: '1:1', portrait: '4:5', story: '9:16' }[fmt] || fmt);

  return (
    <div className="sw-gallery" data-testid="sw-output-gallery">
      <div className="sw-gallery-header">
        <h3>Generated Content</h3>
        <button className="sw-download-all" onClick={downloadAll} data-testid="sw-download-all">
          <Download size={16} /> Download All ({outputs.filter(o => !o.error).length})
        </button>
      </div>
      <div className="sw-gallery-grid">
        {outputs.map((out, i) => (
          <div key={i} className={`sw-gallery-item ${out.format === 'story' ? 'sw-gallery-story' : ''} ${out.format === 'portrait' ? 'sw-gallery-portrait' : ''}`}>
            {out.error ? (
              <div className="sw-gallery-error"><AlertCircle size={20} /><span>{out.error}</span></div>
            ) : out.is_video ? (
              <>
                <video src={`${API}${out.url}`} controls preload="metadata" className="sw-gallery-video" />
                <div className="sw-gallery-actions">
                  <div className="sw-gallery-meta">
                    <span className="sw-gallery-label">{out.template_id.replace(/_/g, ' ')} (Video)</span>
                    <span className="sw-gallery-size">{sizeLabel(out.format)}</span>
                  </div>
                  <a href={`${API}${out.url}`} download={out.filename} className="sw-gallery-dl" data-testid={`sw-dl-${i}`}>
                    <Download size={14} />
                  </a>
                </div>
              </>
            ) : (
              <>
                <div className="sw-coming-visual"><ImageIcon size={24} /><span>Coming soon</span></div>
                <div className="sw-gallery-actions">
                  <div className="sw-gallery-meta">
                    <span className="sw-gallery-label">{out.template_id.replace(/_/g, ' ')}</span>
                    <span className="sw-gallery-size">{sizeLabel(out.format)}</span>
                  </div>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const DAYS = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

// ── Saved Weeks Panel (sidebar in step 1) ──
function SavedWeeksPanel({ branchId, onLoadWeek }) {
  const [weeks, setWeeks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!branchId) { setLoading(false); return; }
    axios.get(`${API}/api/social-week/scheduler/saved-weeks?branch_id=${branchId}`)
      .then(res => setWeeks(res.data.weeks || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [branchId]);

  if (loading) return null;
  if (weeks.length === 0) return null;

  return (
    <div className="sw-saved-weeks" data-testid="sw-saved-weeks">
      <button
        className="sw-saved-toggle"
        onClick={() => setExpanded(v => !v)}
        data-testid="sw-saved-toggle"
      >
        <FileText size={14} />
        <span>Saved Weeks ({weeks.length})</span>
        <ChevronRight size={13} className={`sw-saved-chevron ${expanded ? 'sw-saved-chevron-open' : ''}`} />
      </button>
      {expanded && (
        <div className="sw-saved-list">
          {weeks.map(w => (
            <button
              key={w.week_id}
              className="sw-saved-item"
              onClick={() => onLoadWeek(w.week_id)}
              data-testid={`sw-saved-week-${w.week_id}`}
            >
              <div className="sw-saved-info">
                <span className="sw-saved-label">{w.week_label}</span>
                <span className="sw-saved-meta">
                  {w.post_count} post{w.post_count !== 1 ? 's' : ''}
                  {w.created_by_name ? ` · ${w.created_by_name}` : ''}
                </span>
              </div>
              <span className={`sw-saved-status sw-saved-${w.status}`}>{w.status}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Caption Guidelines Panel ──
function CaptionGuidelinesPanel({ branchId, onClose }) {
  const [guidelines, setGuidelines] = useState({ sign_off: '', hashtags: '', tone: 'Professional and friendly', custom_instructions: '' });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!branchId) return;
    axios.get(`${API}/api/social-week/scheduler/caption-guidelines/${branchId}`)
      .then(res => { setGuidelines(res.data); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, [branchId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/social-week/scheduler/caption-guidelines/${branchId}`, guidelines);
      toast.success('Caption guidelines saved');
      onClose();
    } catch {
      toast.error('Failed to save guidelines');
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;

  return (
    <div className="pw-guidelines-overlay" onClick={onClose} data-testid="caption-guidelines-modal">
      <div className="pw-guidelines-modal" onClick={e => e.stopPropagation()}>
        <div className="pw-guidelines-header">
          <Settings2 size={16} />
          <span>Caption Guidelines</span>
          <button className="pw-guidelines-close" onClick={onClose}><X size={16} /></button>
        </div>
        <p className="pw-guidelines-hint">Set branch-wide defaults for AI-generated captions</p>
        <div className="pw-guidelines-fields">
          <label className="pw-guide-label">
            Tone / Style
            <input
              className="pw-guide-input"
              value={guidelines.tone}
              onChange={e => setGuidelines(g => ({ ...g, tone: e.target.value }))}
              placeholder="e.g. Professional and friendly"
              data-testid="guide-tone"
            />
          </label>
          <label className="pw-guide-label">
            Sign-off (always appended)
            <input
              className="pw-guide-input"
              value={guidelines.sign_off}
              onChange={e => setGuidelines(g => ({ ...g, sign_off: e.target.value }))}
              placeholder="e.g. Call us on 01234 567890"
              data-testid="guide-signoff"
            />
          </label>
          <label className="pw-guide-label">
            Hashtags
            <input
              className="pw-guide-input"
              value={guidelines.hashtags}
              onChange={e => setGuidelines(g => ({ ...g, hashtags: e.target.value }))}
              placeholder="e.g. #NewListing #Cobham #EstateAgent"
              data-testid="guide-hashtags"
            />
          </label>
          <label className="pw-guide-label">
            Custom instructions
            <textarea
              className="pw-guide-textarea"
              value={guidelines.custom_instructions}
              onChange={e => setGuidelines(g => ({ ...g, custom_instructions: e.target.value }))}
              placeholder="e.g. Always mention our free valuation offer"
              rows={2}
              data-testid="guide-custom"
            />
          </label>
        </div>
        <button className="pw-guidelines-save" onClick={handleSave} disabled={saving} data-testid="guide-save">
          {saving ? <Loader2 size={14} className="spin" /> : <Save size={14} />}
          Save Guidelines
        </button>
      </div>
    </div>
  );
}

// ── Day Post Card (inside Program Week) — shows the GENERATED image ──
function DayPostCard({ post, onCaptionChange, onCopyCaption, onRemove, onTimeChange, onGenerateCaption, onSwapPost, apiBase, isCenter }) {
  const imageUrl = post.url ? `${apiBase}${post.url}` : null;
  const isVideo = post.isVideo === true || post.is_video === true;
  const videoSrc = post.videoUrl || (isVideo && imageUrl ? imageUrl : null);
  const formatLabel = { square: '1:1', portrait: '4:5', story: '9:16' }[post.format] || '4:5';
  const aspectClass = post.format === 'story' ? 'pw-visual-story' : post.format === 'square' ? 'pw-visual-square' : '';
  const [generatingCaption, setGeneratingCaption] = useState(false);

  const handleAICaption = async () => {
    setGeneratingCaption(true);
    try {
      await onGenerateCaption(post.uid);
    } finally {
      setGeneratingCaption(false);
    }
  };

  return (
    <div className={`pw-card ${isCenter ? 'pw-card-center' : 'pw-card-side'}`} data-testid={`pw-post-${post.uid}`}>
      <div className={`pw-card-visual ${aspectClass}`}>
        {isVideo && videoSrc ? (
          <video
            src={videoSrc}
            controls
            preload="metadata"
            className="pw-card-video"
            data-testid={`pw-video-${post.uid}`}
          />
        ) : (
          <div className="pw-coming-visual"><ImageIcon size={28} /><span>Coming soon</span></div>
        )}
        <span className="pw-card-badge">
          {isVideo ? 'Video' : post.template_id?.replace(/_/g, ' ')}
        </span>
        <span className="pw-card-format-badge" data-testid={`pw-format-${post.uid}`}>{formatLabel}</span>
        <div className="pw-card-overlay-actions">
          {onSwapPost && (
            <button
              className="pw-card-swap-btn"
              onClick={() => onSwapPost(post.uid)}
              title="Swap for a different post"
              data-testid={`pw-swap-${post.uid}`}
            >
              <RefreshCw size={12} />
            </button>
          )}
          {onRemove && (
            <button
              className="pw-card-remove-btn"
              onClick={() => onRemove(post.uid)}
              title="Remove post"
              data-testid={`pw-remove-${post.uid}`}
            >
              <X size={12} />
            </button>
          )}
        </div>
      </div>
      <div className="pw-card-body">
        {post.listing && (
          <span className="pw-card-address">
            {isVideo ? (post.listing.filename || 'Social Video') : (post.listing.full_address || `Order #${post.listing.order_id}`)}
          </span>
        )}
        <div className="pw-caption-wrap">
          <textarea
            className="pw-card-caption"
            value={post.caption || ''}
            onChange={e => onCaptionChange(post.uid, e.target.value)}
            placeholder="Write your caption or click AI to generate..."
            rows={3}
            data-testid={`pw-caption-${post.uid}`}
          />
          <button
            className={`pw-ai-caption-btn ${generatingCaption ? 'pw-ai-loading' : ''}`}
            onClick={handleAICaption}
            disabled={generatingCaption}
            title="Generate AI caption"
            data-testid={`pw-ai-caption-${post.uid}`}
          >
            {generatingCaption ? <Loader2 size={13} className="spin" /> : <Sparkles size={13} />}
          </button>
        </div>
        <div className="pw-card-actions">
          <button className="pw-card-copy" onClick={() => onCopyCaption(post.uid)} data-testid={`pw-copy-${post.uid}`}>
            <Copy size={12} /> Copy
          </button>
          <div className="pw-card-schedule">
            <input
              type="time"
              className="pw-card-time"
              value={post.time || ''}
              onChange={e => onTimeChange(post.uid, e.target.value)}
              data-testid={`pw-time-${post.uid}`}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Program Week — Day-based card view with save, AI captions, time pickers ──
function ProgramWeekView({ slots, branchId, onUpdateCaption, onCopyCaption, onRemoveFromDay, onTimeChange, onGenerateCaption, onSaveWeek, onSwapPost, onBack, onNext, apiBase, saving, outputs }) {
  const [dayIndex, setDayIndex] = useState(0);
  const [swapTarget, setSwapTarget] = useState(null); // uid of post being swapped
  const currentDay = DAYS[dayIndex];

  const daySlots = slots.filter(s => s.day === currentDay);
  const dayCounts = DAYS.map(d => slots.filter(s => s.day === d).length);

  // Available outputs that could be swapped in (not already in the week)
  const usedUrls = new Set(slots.map(s => s.url).filter(Boolean));
  const swapCandidates = (outputs || []).filter(o => o.url && !usedUrls.has(o.url));

  return (
    <div className="pw-wrap" data-testid="program-week-view">
      {/* Compact top bar */}
      <div className="pw-topbar">
        <button className="pw-topbar-btn" onClick={onBack} data-testid="pw-back-btn">
          <ChevronLeft size={15} /> Back
        </button>
        <span className="pw-topbar-title">Program Your Week</span>
        <div className="pw-topbar-actions">
          <button
            className="pw-topbar-btn pw-topbar-save"
            onClick={onSaveWeek}
            disabled={saving}
            data-testid="pw-save-btn"
          >
            {saving ? <Loader2 size={14} className="spin" /> : <Save size={14} />}
            Save Week
          </button>
          <button className="pw-topbar-btn pw-topbar-download" onClick={onNext} data-testid="pw-next-btn">
            <Download size={14} /> Download
          </button>
        </div>
      </div>

      {/* Day pills */}
      <div className="pw-days" data-testid="pw-day-nav">
        {DAYS.map((day, i) => (
          <button
            key={day}
            className={`pw-day ${i === dayIndex ? 'pw-day-on' : ''}`}
            onClick={() => setDayIndex(i)}
            data-testid={`pw-day-${day}`}
          >
            {day.slice(0, 3)}
            {dayCounts[i] > 0 && <span className="pw-day-n">{dayCounts[i]}</span>}
          </button>
        ))}
      </div>

      {/* Cards row */}
      <div className="pw-cards-row">
        {daySlots.length === 0 ? (
          <div className="pw-empty">
            <Calendar size={24} />
            <span>No posts for {currentDay.toLowerCase()}</span>
          </div>
        ) : (
          daySlots.map(post => (
            <DayPostCard
              key={post.uid}
              post={post}
              isCenter={true}
              onCaptionChange={onUpdateCaption}
              onCopyCaption={onCopyCaption}
              onRemove={onRemoveFromDay}
              onTimeChange={onTimeChange}
              onGenerateCaption={onGenerateCaption}
              onSwapPost={(uid) => setSwapTarget(uid)}
              apiBase={apiBase}
            />
          ))
        )}
      </div>

      {/* Swap Post Modal */}
      {swapTarget && (
        <div className="pw-swap-overlay" onClick={() => setSwapTarget(null)} data-testid="pw-swap-modal">
          <div className="pw-swap-modal" onClick={e => e.stopPropagation()}>
            <div className="pw-swap-header">
              <RefreshCw size={15} />
              <span>Swap Post</span>
              <button className="pw-swap-close" onClick={() => setSwapTarget(null)}><X size={16} /></button>
            </div>
            <p className="pw-swap-hint">Choose a different generated image to replace this post</p>
            {swapCandidates.length === 0 ? (
              <div className="pw-swap-empty">No other generated images available to swap</div>
            ) : (
              <div className="pw-swap-grid">
                {swapCandidates.map((out, i) => {
                  const sizeLabel = { square: '1:1', portrait: '4:5', story: '9:16' }[out.format] || out.format;
                  return (
                    <button
                      key={i}
                      className="pw-swap-item"
                      onClick={() => {
                        onSwapPost(swapTarget, out);
                        setSwapTarget(null);
                      }}
                      data-testid={`pw-swap-option-${i}`}
                    >
                      {out.is_video ? (
                        <Video size={24} className="pw-swap-icon" />
                      ) : (
                        <img src={`${apiBase}${out.url}`} alt="" className="pw-swap-thumb" />
                      )}
                      <div className="pw-swap-item-info">
                        <span className="pw-swap-item-template">{(out.template_id || '').replace(/_/g, ' ')}</span>
                        <span className="pw-swap-item-size">{sizeLabel}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
export default function SocialWeekBuilder({ onBack }) {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [showPhonePreview, setShowPhonePreview] = useState(false);
  const [showAddPicker, setShowAddPicker] = useState(false);
  const [listings, setListings] = useState([]);
  const [thumbnails, setThumbnails] = useState({});
  const [baseTemplates, setBaseTemplates] = useState([]);
  const [slots, setSlots] = useState([]);
  const [logos, setLogos] = useState({ dark: null, light: null });
  const [logoVariant, setLogoVariant] = useState('dark');
  const [logoPosition, setLogoPosition] = useState('north');
  const [logoScale, setLogoScale] = useState(60);
  const [logoPadding, setLogoPadding] = useState(10);
  const [logoOpacity, setLogoOpacity] = useState(100);
  const [loading, setLoading] = useState(true);
  const [listingsLoading, setListingsLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [outputs, setOutputs] = useState([]);
  const pollRef = useRef(null);
  // Cache property images so we don't re-fetch for the same order
  const imagesCacheRef = useRef({});
  // Branch videos state
  const [branchVideos, setBranchVideos] = useState([]);
  const [videosLoading, setVideosLoading] = useState(false);

  // Branch selector for super admins / client managers
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(user?.branch_id || '');
  const isAdmin = user?.role === 'super_admin' || user?.role === 'client_manager';

  // Program Week state: slots with day assignments and captions
  const [weekSlots, setWeekSlots] = useState([]);

  // Load branches for admin dropdown
  useEffect(() => {
    if (!isAdmin) return;
    axios.get(`${API}/api/social-week/branches`)
      .then(res => {
        const allBranches = res.data || [];
        setBranches(allBranches);
        if (!selectedBranchId && allBranches.length > 0) {
          setSelectedBranchId(allBranches[0].id);
        }
      })
      .catch(() => {});
  }, [isAdmin]);

  // Load data when branch changes
  useEffect(() => {
    const branchId = selectedBranchId || user?.branch_id || '';
    if (!branchId && isAdmin) return; // Admin needs a branch selection
    const load = async () => {
      try {
        // 1. Load templates + logos (fast, small payloads)
        const [tmplRes, logoRes] = await Promise.all([
          axios.get(`${API}/api/social-week/templates?mode=single&branch_id=${branchId}`),
          axios.get(`${API}/api/social-week/logos`),
        ]);
        let templates = tmplRes.data.templates || [];
        const branchConfig = tmplRes.data.branch_config;

        // Filter templates by branch config (enabled post types + sizes)
        if (branchConfig) {
          const enabled = branchConfig.enabled_post_types || [];
          const disabledSizes = branchConfig.disabled_sizes || {};

          // Map template id -> post type for filtering
          const idToPostType = {
            just_listed: 'Just Listed', new_listing: 'Just Listed',
            featured: 'Featured', sneak_peek: 'Sneak Peek',
            price_listing: 'Price Reduction', sold: 'Sold', sold_location: 'Sold',
            under_offer: 'Under Offer', carousel_1: 'Carousel', carousel_2: 'Carousel', carousel_3: 'Carousel',
            story_image: 'Story', story_sold: 'Sold',
            collage_portrait: 'Collage', collage_story: 'Collage', collage: 'Collage',
          };
          templates = templates.filter(t => {
            const pt = idToPostType[t.id] || t.id;
            return enabled.includes(pt);
          });
        }

        setBaseTemplates(templates);
        // Pre-populate with first 4 available templates
        const defaultIds = ['just_listed', 'featured', 'sold', 'carousel_1'];
        const defaults = defaultIds
          .map(id => templates.find(t => t.id === id))
          .filter(Boolean);
        setSlots(defaults.length > 0 ? defaults.map(makeSlot) : templates.slice(0, 4).map(makeSlot));
        setLogos(logoRes.data || { dark: null, light: null });
        setLoading(false);

        // 1b. Load branch videos (filtered by selected branch)
        setVideosLoading(true);
        axios.get(`${API}/api/social-week/branch-videos?branch_id=${branchId}`)
          .then(res => setBranchVideos(res.data.videos || []))
          .catch(() => setBranchVideos([]))
          .finally(() => setVideosLoading(false));

        // 2. Load listings from dedicated Social Week endpoint (completed orders with photos)
        try {
          const listRes = await axios.get(`${API}/api/social-week/listings?branch_id=${branchId}`);
          const allListings = listRes.data.listings || [];
          const seen = new Set();
          const uniqueListings = allListings.filter(l => { if (seen.has(l.order_id)) return false; seen.add(l.order_id); return true; });
          setListings(uniqueListings);
          prefetchImages(uniqueListings);

          // If backend is still scanning WMS, poll until complete
          if (listRes.data.scanning) {
            setListingsLoading(true);
            setScanning(true);
            const poll = async (attempt = 0) => {
              if (attempt >= 30) { setListingsLoading(false); setScanning(false); return; }
              await new Promise(r => setTimeout(r, 5000));
              try {
                const retry = await axios.get(`${API}/api/social-week/listings?branch_id=${branchId}`);
                const retryListings = retry.data.listings || [];
                const rs = new Set();
                const unique = retryListings.filter(l => { if (rs.has(l.order_id)) return false; rs.add(l.order_id); return true; });
                // Merge with existing listings — keep manually-added ones
                setListings(prev => {
                  const apiIds = new Set(unique.map(l => l.order_id));
                  const manuallyAdded = prev.filter(l => !apiIds.has(l.order_id));
                  return [...unique, ...manuallyAdded];
                });
                prefetchImages(unique);
                if (!retry.data.scanning) {
                  setListingsLoading(false);
                  setScanning(false);
                  return;
                }
                poll(attempt + 1);
              } catch { setListingsLoading(false); setScanning(false); }
            };
            poll();
          } else {
            setListingsLoading(false);
          }
        } catch (err) {
          console.error('Failed to load listings:', err);
          setListingsLoading(false);
        }

      } catch (err) {
        toast.error('Failed to load data');
        setLoading(false);
      }
    };

    // Fast batch thumbnail loading for property bin
    // First call returns cached results instantly; background R2 scan starts for uncached
    // Then poll on a fast schedule (3s, 7s, 12s, 20s, 30s) — stops the moment all are found
    const prefetchImages = async (listingsToFetch) => {
      const oids = listingsToFetch
        .map(l => l.order_id)
        .filter(oid => oid && !imagesCacheRef.current[oid]);
      if (oids.length === 0) return;
      try {
        const res = await axios.post(`${API}/api/social-week/batch-thumbnails`, { order_ids: oids });
        const thumbs = res.data.thumbnails || {};
        if (Object.keys(thumbs).length > 0) {
          setThumbnails(prev => ({ ...prev, ...thumbs }));
        }
        let missing = oids.filter(oid => !thumbs[String(oid)]);
        if (missing.length === 0) return;

        // Tight poll schedule — gives backend time to parallel-fetch from WMS
        const delays = [3000, 4000, 5000, 8000, 10000];
        for (const wait of delays) {
          if (missing.length === 0) break;
          await new Promise(r => setTimeout(r, wait));
          try {
            const r2 = await axios.post(`${API}/api/social-week/batch-thumbnails`, { order_ids: missing });
            const t2 = r2.data.thumbnails || {};
            if (Object.keys(t2).length > 0) {
              setThumbnails(prev => ({ ...prev, ...t2 }));
              missing = missing.filter(oid => !t2[String(oid)]);
            }
          } catch { /* continue polling */ }
        }
      } catch (err) {
        console.warn('Batch thumbnail fetch failed:', err);
      }
    };

    load();
  }, [selectedBranchId]);

  // Poll job status
  useEffect(() => {
    if (!jobId || !generating) return;
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/api/social-week/job/${jobId}`);
        const data = res.data;
        setJobStatus(data);
        if (data.status === 'complete') {
          const generatedOutputs = data.outputs || [];
          setOutputs(generatedOutputs);
          setGenerating(false);
          clearInterval(pollRef.current);
          const videoCount = generatedOutputs.filter(o => o.is_video && o.filename).length;
          const imageCount = generatedOutputs.filter(o => !o.is_video && o.filename).length;
          const errorOutputs = generatedOutputs.filter(o => !o.filename && o.error);
          if (errorOutputs.length > 0 && videoCount + imageCount === 0) {
            // Pure failure — surface the first error so user can see what's wrong
            const firstErr = errorOutputs[0];
            toast.error(
              `Generation failed for ${firstErr.template_id} (${firstErr.format}): ${String(firstErr.error).slice(0, 200)}`,
              { duration: 10000 }
            );
          } else if (errorOutputs.length > 0) {
            // Partial — mention what worked + how many failed
            toast.warning(
              `Generated ${imageCount} images${videoCount > 0 ? ` + ${videoCount} videos` : ''}, but ${errorOutputs.length} slot${errorOutputs.length > 1 ? 's' : ''} failed: ${String(errorOutputs[0].error).slice(0, 150)}`,
              { duration: 8000 }
            );
          } else {
            const msg = videoCount > 0 && imageCount > 0
              ? `Generated ${imageCount} images + ${videoCount} videos!`
              : videoCount > 0
              ? `Generated ${videoCount} branded video${videoCount > 1 ? 's' : ''}!`
              : `Generated ${imageCount} images!`;
            toast.success(msg);
          }
          
          // Distribute outputs across days for Program Week
          // Group by slot (template_id + listing_id) so multiple sizes stay on same day
          const validOutputs = generatedOutputs.filter(o => o.filename);
          const slotGroups = {};
          validOutputs.forEach(output => {
            const key = output.is_video
              ? `video_${output.template_id}`
              : `${output.template_id}_${output.listing_id}`;
            if (!slotGroups[key]) slotGroups[key] = [];
            slotGroups[key].push(output);
          });

          const distributed = [];
          let dayIdx = 0;
          Object.values(slotGroups).forEach(group => {
            const day = DAYS[dayIdx % 7];
            group.forEach(output => {
              distributed.push({
                ...output,
                uid: `out_${distributed.length}`,
                day,
                caption: '',
                isVideo: output.is_video || false,
                videoUrl: output.is_video ? `${API}${output.url}` : null,
                listing: filledSlots.find(s => {
                  if (output.is_video) return s.listing?.isVideo;
                  return s.listing?.order_id === output.listing_id;
                })?.listing || null,
              });
            });
            dayIdx++;
          });
          setWeekSlots(distributed);
          setStep(3);
        } else if (data.status === 'error') {
          setGenerating(false);
          clearInterval(pollRef.current);
          toast.error(data.error || 'Generation failed');
        }
      } catch { }
    }, 2000);
    return () => clearInterval(pollRef.current);
  }, [jobId, generating]);

  // Fetch property images when a listing is assigned to a slot
  const fetchPropertyImages = useCallback(async (uid, orderId) => {
    // Check cache first
    if (imagesCacheRef.current[orderId] && imagesCacheRef.current[orderId].length > 0) {
      const cached = imagesCacheRef.current[orderId];
      setSlots(prev => prev.map(s => {
        if (s.uid !== uid) return s;
        const firstUrl = s.selectedImageUrl || cached[0].url;
        return {
          ...s,
          images: cached,
          imagesLoading: false,
          selectedImageUrl: firstUrl,
          perSizeImages: { square: s.perSizeImages?.square || firstUrl, portrait: s.perSizeImages?.portrait || firstUrl, story: s.perSizeImages?.story || firstUrl },
        };
      }));
      return;
    }

    // Mark loading
    setSlots(prev => prev.map(s => s.uid === uid ? { ...s, imagesLoading: true } : s));

    try {
      const res = await axios.get(`${API}/api/social-week/property-images/${orderId}`);
      const imgs = res.data.images || [];
      const scanning = res.data.scanning;
      if (imgs.length > 0) {
        imagesCacheRef.current[orderId] = imgs;
        setSlots(prev => prev.map(s => {
          if (s.uid !== uid) return s;
          const firstUrl = s.selectedImageUrl || imgs[0].url;
          return {
            ...s,
            images: imgs,
            imagesLoading: false,
            selectedImageUrl: firstUrl,
            perSizeImages: { square: s.perSizeImages?.square || firstUrl, portrait: s.perSizeImages?.portrait || firstUrl, story: s.perSizeImages?.story || firstUrl },
          };
        }));
      } else if (scanning) {
        // R2/WMS scan in progress — poll for this slot.
        // We track empty-result polls so a transient failure can be retried
        // up to a few times instead of declaring "No photos" prematurely.
        let emptyAttempts = 0;
        const poll = setInterval(async () => {
          try {
            const pollRes = await axios.get(`${API}/api/social-week/property-images/${orderId}?refresh=${emptyAttempts > 0 ? 'true' : 'false'}`);
            const pollImgs = pollRes.data.images || [];
            if (pollImgs.length > 0) {
              clearInterval(poll);
              imagesCacheRef.current[orderId] = pollImgs;
              setThumbnails(prev => ({ ...prev, [String(orderId)]: pollImgs[0].url }));
              setSlots(prev => prev.map(s => {
                if (s.uid !== uid) return s;
                const firstUrl = s.selectedImageUrl || pollImgs[0].url;
                return {
                  ...s,
                  images: pollImgs,
                  imagesLoading: false,
                  selectedImageUrl: firstUrl,
                  perSizeImages: { square: s.perSizeImages?.square || firstUrl, portrait: s.perSizeImages?.portrait || firstUrl, story: s.perSizeImages?.story || firstUrl },
                };
              }));
            } else if (!pollRes.data.scanning) {
              // Scan finished but produced 0 images — could be a transient WMS
              // failure. Retry up to 3 times by forcing a refresh, then give up.
              emptyAttempts += 1;
              if (emptyAttempts >= 3) {
                clearInterval(poll);
                setSlots(prev => prev.map(s => s.uid === uid ? { ...s, imagesLoading: false, images: [] } : s));
              }
              // else: leave imagesLoading: true and let the next interval tick re-trigger with refresh=true
            }
          } catch {
            clearInterval(poll);
          }
        }, 3000);
        setTimeout(() => clearInterval(poll), 90000);
      } else {
        // Backend returned scanning=false + images=[] up front. Could be a
        // poisoned cache entry from a past transient failure. Force a refresh.
        try {
          const r2 = await axios.get(`${API}/api/social-week/property-images/${orderId}?refresh=true`);
          const r2Imgs = r2.data.images || [];
          if (r2Imgs.length > 0) {
            imagesCacheRef.current[orderId] = r2Imgs;
            setSlots(prev => prev.map(s => {
              if (s.uid !== uid) return s;
              const firstUrl = s.selectedImageUrl || r2Imgs[0].url;
              return {
                ...s,
                images: r2Imgs,
                imagesLoading: false,
                selectedImageUrl: firstUrl,
                perSizeImages: { square: s.perSizeImages?.square || firstUrl, portrait: s.perSizeImages?.portrait || firstUrl, story: s.perSizeImages?.story || firstUrl },
              };
            }));
            return;
          }
          if (r2.data.scanning) {
            // Refresh kicked off a fresh scan — fall back to polling
            const poll = setInterval(async () => {
              try {
                const pr = await axios.get(`${API}/api/social-week/property-images/${orderId}`);
                const pi = pr.data.images || [];
                if (pi.length > 0) {
                  clearInterval(poll);
                  imagesCacheRef.current[orderId] = pi;
                  setSlots(prev => prev.map(s => {
                    if (s.uid !== uid) return s;
                    const firstUrl = s.selectedImageUrl || pi[0].url;
                    return { ...s, images: pi, imagesLoading: false, selectedImageUrl: firstUrl, perSizeImages: { square: s.perSizeImages?.square || firstUrl, portrait: s.perSizeImages?.portrait || firstUrl, story: s.perSizeImages?.story || firstUrl } };
                  }));
                } else if (!pr.data.scanning) {
                  clearInterval(poll);
                  setSlots(prev => prev.map(s => s.uid === uid ? { ...s, imagesLoading: false, images: [] } : s));
                }
              } catch { clearInterval(poll); }
            }, 3000);
            setTimeout(() => clearInterval(poll), 90000);
            return;
          }
        } catch { /* fall through */ }
        setSlots(prev => prev.map(s => s.uid === uid ? { ...s, imagesLoading: false, images: [] } : s));
      }
    } catch {
      setSlots(prev => prev.map(s => s.uid === uid ? { ...s, imagesLoading: false, images: [] } : s));
    }
  }, []);

  const handleDrop = useCallback((uid, data) => {
    if (data.isVideo) {
      // Video dropped — no image fetching needed
      setSlots(prev => prev.map(s => s.uid === uid ? {
        ...s,
        listing: data,
        images: [],
        imagesLoading: false,
        selectedImageUrl: null,
      } : s));
      toast.success('Video assigned to slot');
    } else {
      // Property listing dropped — fetch images
      setSlots(prev => prev.map(s => s.uid === uid ? {
        ...s,
        listing: data,
        images: [],
        imagesLoading: true,
        selectedImageUrl: null,
      } : s));
      fetchPropertyImages(uid, data.order_id);
    }
  }, [fetchPropertyImages]);

  const handleRemove = useCallback((uid) => {
    setSlots(prev => {
      const slot = prev.find(s => s.uid === uid);
      if (!slot) return prev;
      const sameType = prev.filter(s => s.templateId === slot.templateId);
      if (sameType.length <= 1) {
        return prev.map(s => s.uid === uid ? { ...s, listing: null, images: [], selectedImageUrl: null } : s);
      }
      return prev.filter(s => s.uid !== uid);
    });
  }, []);

  const handleSizesChange = useCallback((uid, newSizes) => {
    setSlots(prev => prev.map(s => s.uid === uid ? { ...s, sizes: newSizes } : s));
  }, []);

  const handleSelectImage = useCallback((uid, url) => {
    setSlots(prev => prev.map(s => {
      if (s.uid !== uid) return s;
      return {
        ...s,
        selectedImageUrl: url,
        perSizeImages: { ...s.perSizeImages, [s.activeTab]: url },
      };
    }));
  }, []);

  const handleFocalPointChange = useCallback((uid, focalPoint) => {
    setSlots(prev => prev.map(s => {
      if (s.uid !== uid) return s;
      return {
        ...s,
        focalPoint,
        perSizeFocalPoints: { ...s.perSizeFocalPoints, [s.activeTab]: focalPoint },
      };
    }));
  }, []);

  const handleUpdateCollage = useCallback((uid, updates) => {
    setSlots(prev => prev.map(s => s.uid === uid ? { ...s, ...updates } : s));
  }, []);

  const handleTabChange = useCallback((uid, tab) => {
    setSlots(prev => prev.map(s => {
      if (s.uid !== uid) return s;
      const updates = { activeTab: tab };
      if (s.isCollage) {
        updates.collagePanelCount = tab === 'story' ? 3 : 2;
      }
      return { ...s, ...updates };
    }));
  }, []);

  const handleDuplicate = useCallback((uid) => {
    setSlots(prev => {
      const idx = prev.findIndex(s => s.uid === uid);
      if (idx < 0) return prev;
      const src = prev[idx];
      const dup = {
        ...src,
        uid: nextUid(),
        sizes: { ...src.sizes },
        listing: src.listing ? { ...src.listing } : null,
        images: [...src.images],
        selectedImageUrl: src.selectedImageUrl,
        focalPoint: { ...src.focalPoint },
        collageImages: src.collageImages ? [...src.collageImages] : [],
        collageImageIdx: src.collageImageIdx ? [...src.collageImageIdx] : [],
        collageFocalPoints: src.collageFocalPoints ? src.collageFocalPoints.map(fp => ({ ...fp })) : [],
      };
      const next = [...prev];
      next.splice(idx + 1, 0, dup);
      return next;
    });
    toast.success('Slot duplicated');
  }, []);

  const handleDeleteSlot = useCallback((uid) => {
    setSlots(prev => {
      if (prev.length <= 1) {
        toast.error('Need at least one template slot');
        return prev;
      }
      return prev.filter(s => s.uid !== uid);
    });
  }, []);

  const handleAddSlot = useCallback((templateId) => {
    const tpl = baseTemplates.find(t => t.id === templateId);
    if (!tpl) return;
    setSlots(prev => [...prev, makeSlot(tpl)]);
    setShowAddPicker(false);
    toast.success(`${tpl.name} slot added`);
  }, [baseTemplates]);

  const handleUploadLogo = async (formData, variant) => {
    setUploading(true);
    try {
      await axios.post(`${API}/api/social-week/upload-logo`, formData);
      const logoRes = await axios.get(`${API}/api/social-week/logos`);
      setLogos(logoRes.data || { dark: null, light: null });
      toast.success(`${variant} logo uploaded`);
    } catch (err) {
      toast.error('Logo upload failed');
    } finally {
      setUploading(false);
    }
  };

  const filledSlots = slots.filter(s => s.listing);
  // A slot only counts toward the total once it has at least one size enabled
  const videoCount = filledSlots.filter(s => s.listing?.isVideo).reduce((sum, s) => sum + Object.values(s.sizes).filter(Boolean).length, 0);
  const imageCount = filledSlots.filter(s => !s.listing?.isVideo).reduce((sum, s) => sum + Object.values(s.sizes).filter(Boolean).length, 0);
  const totalImages = imageCount + videoCount;
  // Slots that have content but no size enabled — surfaced for warnings
  const unsizedSlots = filledSlots.filter(s => Object.values(s.sizes).every(v => !v));

  // ── Program Week Handlers ──
  const handleProceedToWeek = useCallback(() => {
    // Distribute filled slots across days of the week
    const filled = slots.filter(s => s.listing);
    if (filled.length === 0) {
      toast.error('Assign at least one property to a template slot first');
      return;
    }
    const distributed = filled.map((s, i) => ({
      ...s,
      day: DAYS[i % 7],
      caption: '',
    }));
    setWeekSlots(distributed);
    setStep(2);
  }, [slots]);

  const handleWeekCaptionChange = useCallback((uid, caption) => {
    setWeekSlots(prev => prev.map(s => s.uid === uid ? { ...s, caption } : s));
  }, []);

  const handleWeekCopyCaption = useCallback((uid) => {
    const post = weekSlots.find(s => s.uid === uid);
    if (post?.caption) {
      navigator.clipboard.writeText(post.caption).then(() => toast.success('Caption copied!')).catch(() => toast.error('Copy failed'));
    } else {
      toast.error('No caption to copy');
    }
  }, [weekSlots]);

  const handleRemoveFromDay = useCallback((uid) => {
    setWeekSlots(prev => prev.filter(s => s.uid !== uid));
  }, []);

  const handleWeekTimeChange = useCallback((uid, time) => {
    setWeekSlots(prev => prev.map(s => s.uid === uid ? { ...s, time } : s));
  }, []);

  const [savingWeek, setSavingWeek] = useState(false);

  const handleGenerateCaption = useCallback(async (uid) => {
    const post = weekSlots.find(s => s.uid === uid);
    if (!post) return;

    const address = post.listing?.full_address || post.listing?.filename || '';
    const price = post.listing?.price ? `£${Number(String(post.listing.price).replace(/,/g, '')).toLocaleString()}` : '';

    try {
      const res = await axios.post(`${API}/api/social-week/scheduler/generate-caption`, {
        template_id: post.template_id || 'featured',
        listing_address: address,
        listing_price: price || undefined,
        listing_features: [],
        branch_id: selectedBranchId || user?.branch_id || null,
      });
      const caption = res.data.caption;
      setWeekSlots(prev => prev.map(s => s.uid === uid ? { ...s, caption } : s));
      toast.success('Caption generated');
    } catch (err) {
      toast.error('Failed to generate caption');
    }
  }, [weekSlots, selectedBranchId, user]);

  const handleSaveWeek = useCallback(async () => {
    if (weekSlots.length === 0) {
      toast.error('No posts to save');
      return;
    }
    setSavingWeek(true);
    try {
      const posts = weekSlots.map(s => ({
        day: s.day,
        time: s.time || '',
        template_id: s.template_id,
        format: s.format,
        caption: s.caption || '',
        image_url: s.url ? `${API}${s.url}` : null,
        url: s.url || null,
        filename: s.filename || null,
        is_video: s.isVideo || s.is_video || false,
        listing: s.listing ? {
          order_id: s.listing.order_id,
          full_address: s.listing.full_address,
          filename: s.listing.filename,
        } : null,
      }));

      const branchId = selectedBranchId || user?.branch_id || '';
      const res = await axios.post(`${API}/api/social-week/scheduler/save-week`, {
        branch_id: branchId,
        posts,
      });

      toast.success(`Week saved: ${res.data.week_label}`);

      // Request notification permission after first save
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission().then(perm => {
          if (perm === 'granted') {
            toast.success('Notifications enabled — we\'ll remind you when it\'s time to post');
            // Subscribe for push
            subscribePush();
          }
        });
      } else if ('Notification' in window && Notification.permission === 'granted') {
        subscribePush();
      }
    } catch (err) {
      toast.error('Failed to save week');
    } finally {
      setSavingWeek(false);
    }
  }, [weekSlots, selectedBranchId, user]);

  const subscribePush = useCallback(async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      let sub = await registration.pushManager.getSubscription();
      if (!sub) {
        // Fetch VAPID key from backend
        const vapidRes = await axios.get(`${API}/api/social-week/scheduler/vapid-key`);
        const vapidKey = vapidRes.data.public_key;
        if (!vapidKey) return;
        sub = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey),
        });
      }
      const keys = sub.toJSON().keys || {};
      await axios.post(`${API}/api/social-week/scheduler/push-subscribe`, {
        endpoint: sub.endpoint,
        keys: { p256dh: keys.p256dh || '', auth: keys.auth || '' },
      });
    } catch {
      // Push subscription failed — silent, not critical
    }
  }, []);

  const handleLoadWeek = useCallback(async (weekId) => {
    try {
      const res = await axios.get(`${API}/api/social-week/scheduler/saved-week/${weekId}`);
      const data = res.data;
      const posts = (data.posts || []).map((p, i) => ({
        ...p,
        uid: `loaded_${i}`,
        isVideo: p.is_video || false,
        videoUrl: p.is_video && p.url ? `${API}${p.url}` : null,
      }));
      setWeekSlots(posts);
      setOutputs(posts); // So swap can access them
      setStep(3);
      toast.success(`Loaded: ${data.week_label}`);
    } catch {
      toast.error('Failed to load saved week');
    }
  }, []);

  const handleSwapPost = useCallback((uid, newOutput) => {
    setWeekSlots(prev => prev.map(s => {
      if (s.uid !== uid) return s;
      return {
        ...s,
        url: newOutput.url,
        filename: newOutput.filename,
        template_id: newOutput.template_id,
        format: newOutput.format,
        is_video: newOutput.is_video || false,
        isVideo: newOutput.is_video || false,
        videoUrl: newOutput.is_video ? `${API}${newOutput.url}` : null,
        listing: newOutput.listing || s.listing,
      };
    }));
    toast.success('Post swapped');
  }, []);

  const handleAddToDay = useCallback((day, templateId) => {
    const tpl = baseTemplates.find(t => t.id === templateId);
    if (!tpl) return;
    const newSlot = { ...makeSlot(tpl), day, caption: '' };
    setWeekSlots(prev => [...prev, newSlot]);
    toast.success(`${tpl.name} added to ${day}`);
  }, [baseTemplates]);

  // Total images from week view (for reference — weekSlots are now output objects)
  const weekTotalImages = weekSlots.length;

  const handleGenerate = async () => {
    if (filledSlots.length === 0) {
      toast.error('Drag at least one property or video onto a template slot');
      return;
    }
    if (unsizedSlots.length > 0) {
      const names = unsizedSlots.map(s => s.label || s.templateId || 'a slot').slice(0, 3).join(', ');
      toast.error(
        `Pick at least one size (1:1, 4:5 or 9:16) on ${unsizedSlots.length === 1 ? '' : 'each of'} ${names}${unsizedSlots.length > 3 ? ` +${unsizedSlots.length - 3} more` : ''} before generating.`,
        { duration: 6500 }
      );
      return;
    }
    if (totalImages === 0) {
      toast.error('Assign at least one property or video to a template slot');
      return;
    }

    const expandedSlots = [];
    for (const slot of filledSlots) {
      for (const sizeDef of SIZE_DEFS) {
        if (slot.sizes[sizeDef.key]) {
          if (slot.listing?.isVideo) {
            // Video slot — send to backend for overlay compositing
            expandedSlots.push({
              template_id: slot.templateId,
              listing_id: null,
              format: sizeDef.key,
              image_url: null,
              is_video: true,
              video_url: slot.listing.url,
            });
          } else {
            // Property slot — standard image generation
            const slotData = {
              template_id: slot.templateId,
              listing_id: slot.listing.order_id,
              format: sizeDef.key,
              image_url: slot.perSizeImages?.[sizeDef.key] || slot.selectedImageUrl || null,
              focal_x: slot.perSizeFocalPoints?.[sizeDef.key]?.x ?? slot.focalPoint?.x ?? 50,
              focal_y: slot.perSizeFocalPoints?.[sizeDef.key]?.y ?? slot.focalPoint?.y ?? 50,
            };
            if (slot.isCollage) {
              const tabData = slot.collageImages?.[sizeDef.key] || [];
              slotData.extra_image_urls = tabData.filter(Boolean);
              slotData.panel_focal_points = slot.collageFocalPoints?.[sizeDef.key] || [];
            }
            expandedSlots.push(slotData);
          }
        }
      }
    }

    // Final guard — should never trigger after the checks above, but just in case
    if (expandedSlots.length === 0) {
      toast.error('Nothing to generate — please pick a size on each filled slot.');
      return;
    }

    setGenerating(true);
    setStep(2);
    try {
      const res = await axios.post(`${API}/api/social-week/generate`, {
        mode: 'single',
        slots: expandedSlots,
        logo_variant: logoVariant,
        logo_position: logoPosition,
        logo_scale: logoScale,
        logo_padding: logoPadding,
        logo_opacity: logoOpacity,
        branch_id: selectedBranchId || user?.branch_id || null,
      });
      setJobId(res.data.job_id);
      setJobStatus({ status: 'rendering', total_slides: expandedSlots.length, completed_slides: 0 });
    } catch (err) {
      setGenerating(false);
      setStep(1);
      toast.error('Failed to start generation');
    }
  };

  const handleReset = () => {
    setStep(1);
    const defaultIds = ['just_listed', 'featured', 'sold', 'carousel_1'];
    const defaults = defaultIds
      .map(id => baseTemplates.find(t => t.id === id))
      .filter(Boolean);
    setSlots(defaults.length > 0 ? defaults.map(makeSlot) : baseTemplates.slice(0, 4).map(makeSlot));
    setWeekSlots([]);
    setJobId(null);
    setJobStatus(null);
    setOutputs([]);
    setGenerating(false);
  };

  // Poll for images when backend is scanning R2
  const pollForImages = useCallback((oid) => {
    const poll = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/api/social-week/property-images/${oid}`);
        const imgs = res.data.images || [];
        const scanning = res.data.scanning;
        if (imgs.length > 0) {
          clearInterval(poll);
          imagesCacheRef.current[oid] = imgs;
          setThumbnails(prev => ({ ...prev, [String(oid)]: imgs[0].url }));
          // Update any slot that already has this listing assigned
          setSlots(prev => prev.map(s =>
            s.listing?.order_id === oid && s.images.length === 0
              ? { ...s, images: imgs, imagesLoading: false, selectedImageUrl: s.selectedImageUrl || imgs[0].url }
              : s
          ));
        } else if (!scanning) {
          // Scan finished but no images found
          clearInterval(poll);
          imagesCacheRef.current[oid] = [];
        }
      } catch {
        clearInterval(poll);
      }
    }, 3000);
    // Stop polling after 90 seconds max
    setTimeout(() => clearInterval(poll), 90000);
  }, []);

  const handleAddOrderId = useCallback((orderData) => {
    // Add the looked-up order to the listings array if not already there
    setListings(prev => {
      if (prev.find(l => l.order_id === orderData.order_id)) return prev;
      return [...prev, orderData];
    });
    // Pre-fetch thumbnail for property bin
    const oid = orderData.order_id;
    if (!imagesCacheRef.current[oid]) {
      axios.post(`${API}/api/social-week/batch-thumbnails`, { order_ids: [oid] })
        .then(res => {
          const thumbs = res.data.thumbnails || {};
          if (Object.keys(thumbs).length > 0) {
            setThumbnails(prev => ({ ...prev, ...thumbs }));
          }
        })
        .catch(() => {});
      // Also trigger background full scan for when user drops into slot
      axios.get(`${API}/api/social-week/property-images/${oid}`).catch(() => {});
    }
  }, []);

  if (loading) {
    return (
      <div className="sw-page">
        <AppHeader onBack={onBack} backLabel="Back to Tools" />
        <div className="sw-loading"><Loader2 size={32} className="spin" /><span>Loading Social Week...</span></div>
      </div>
    );
  }

  return (
    <div className={`sw-page ${step === 3 ? 'sw-page-immersive' : ''}`} data-testid="social-week-page">
      <AppHeader onBack={onBack} backLabel="Back to Tools" />
      <div className="sw-container">
        {step !== 3 && (
          <div className="sw-hero">
            <div className="sw-hero-icon"><Calendar size={28} /></div>
            <div>
              <h1 className="sw-title">Social Week</h1>
              <p className="sw-subtitle">Generate a full week of social media content from your property listings</p>
            </div>
            {isAdmin && branches.length > 0 && (
              <select
                className="sw-branch-select"
                data-testid="sw-branch-selector"
                value={selectedBranchId}
                onChange={(e) => {
                  setSelectedBranchId(e.target.value);
                  setListings([]);
                  setThumbnails({});
                  setSlots([]);
                  setBranchVideos([]);
                  imagesCacheRef.current = {};
                  setListingsLoading(true);
                  setScanning(false);
                }}
              >
                {branches.map(b => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            )}
          </div>
        )}

        {step !== 3 && (
          <div className="sw-steps" data-testid="sw-steps">
            {['Select & Assign', 'Generate', 'Program Week', 'Download'].map((label, i) => (
              <div key={i} className={`sw-step ${step > i + 1 ? 'sw-step-done' : ''} ${step === i + 1 ? 'sw-step-active' : ''}`}>
                <div className="sw-step-num">{step > i + 1 ? <CheckCircle size={16} /> : i + 1}</div>
                <span>{label}</span>
              </div>
            ))}
          </div>
        )}

        {step === 1 && (
          <div className="sw-builder">
            <div className="sw-builder-left">
              <PropertyBin listings={listings} thumbnails={thumbnails} onDragStart={() => { }} loading={listingsLoading} scanning={scanning} onAddOrderId={handleAddOrderId} />
              <BranchVideos videos={branchVideos} loading={videosLoading} />
              <SavedWeeksPanel
                branchId={selectedBranchId || user?.branch_id || ''}
                onLoadWeek={handleLoadWeek}
              />
              <button className="sw-preview-week-btn" onClick={() => setShowPhonePreview(true)} data-testid="sw-preview-week-btn">
                <Smartphone size={18} />
                Preview Week
              </button>
            </div>

            <div className="sw-builder-right">
              <div className="sw-templates-header">
                <h3>Template Slots</h3>
                <span className="sw-fill-count">{filledSlots.length} assigned &middot; {imageCount > 0 ? `${imageCount} image${imageCount !== 1 ? 's' : ''}` : ''}{imageCount > 0 && videoCount > 0 ? ' + ' : ''}{videoCount > 0 ? `${videoCount} video${videoCount !== 1 ? 's' : ''}` : ''}</span>
              </div>

              <div className="sw-slots-grid">
                {slots.map(slot => (
                  <TemplateSlotCard
                    key={slot.uid}
                    slot={slot}
                    thumbnail={slot.listing ? thumbnails[slot.listing.order_id] : null}
                    onDrop={handleDrop}
                    onRemove={handleRemove}
                    onDuplicate={handleDuplicate}
                    onDelete={handleDeleteSlot}
                    onSizesChange={handleSizesChange}
                    onSelectImage={handleSelectImage}
                    onFocalPointChange={handleFocalPointChange}
                    onUpdateCollage={handleUpdateCollage}
                    onTabChange={handleTabChange}
                  />
                ))}

                {/* Add Slot Button */}
                <div className="sw-slot sw-slot-add" data-testid="sw-add-slot">
                  {showAddPicker ? (
                    <div className="sw-add-picker">
                      <div className="sw-add-picker-header">
                        <span>Choose template type</span>
                        <button className="sw-add-picker-close" onClick={() => setShowAddPicker(false)}><X size={14} /></button>
                      </div>
                      <div className="sw-add-picker-list">
                        {baseTemplates.map(tpl => (
                          <button key={tpl.id} className="sw-add-picker-item" onClick={() => handleAddSlot(tpl.id)} data-testid={`sw-add-${tpl.id}`}>
                            {tpl.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <button className="sw-add-slot-btn" onClick={() => setShowAddPicker(true)} data-testid="sw-add-slot-btn">
                      <Plus size={28} />
                      <span>Add Slot</span>
                    </button>
                  )}
                </div>
              </div>

              <button className="sw-generate-btn sw-next-btn" onClick={handleGenerate} disabled={totalImages === 0} data-testid="sw-generate-btn">
                <ImageIcon size={18} />
                Generate {videoCount > 0 && imageCount > 0 ? `${imageCount} Image${imageCount !== 1 ? 's' : ''} + ${videoCount} Video${videoCount !== 1 ? 's' : ''}` : videoCount > 0 ? `${videoCount} Video${videoCount !== 1 ? 's' : ''}` : `${imageCount} Image${imageCount !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="sw-generating" data-testid="sw-generating">
            <div className="sw-gen-spinner"><Loader2 size={48} className="spin" /></div>
            <h2>Generating your social content...</h2>
            <p className="sw-gen-progress">{jobStatus?.completed_slides || 0} / {jobStatus?.total_slides || '...'} slides rendered {videoCount > 0 ? '(videos take longer)' : ''}</p>
            <div className="sw-gen-bar">
              <div className="sw-gen-bar-fill" style={{ width: jobStatus?.total_slides ? `${((jobStatus.completed_slides || 0) / jobStatus.total_slides) * 100}%` : '10%' }} />
            </div>
          </div>
        )}

        {step === 3 && (
          <ProgramWeekView
            slots={weekSlots}
            branchId={selectedBranchId || user?.branch_id || ''}
            onUpdateCaption={handleWeekCaptionChange}
            onCopyCaption={handleWeekCopyCaption}
            onRemoveFromDay={handleRemoveFromDay}
            onTimeChange={handleWeekTimeChange}
            onGenerateCaption={handleGenerateCaption}
            onSaveWeek={handleSaveWeek}
            onSwapPost={handleSwapPost}
            onBack={() => setStep(1)}
            onNext={() => setStep(4)}
            apiBase={API}
            saving={savingWeek}
            outputs={outputs}
          />
        )}

        {step === 4 && (
          <>
            <OutputGallery outputs={outputs} />
            <div className="sw-actions-row">
              <button className="sw-reset-btn" onClick={handleReset} data-testid="sw-reset-btn">
                <RefreshCw size={16} /> Start New Batch
              </button>
            </div>
          </>
        )}
      </div>
      {showPhonePreview && <PhonePreview onClose={() => setShowPhonePreview(false)} />}
    </div>
  );
}
